import React, { FC } from "react";
import { Box, Stack } from "@mui/system";
import { Typography, Link as MuiLink, Divider, useTheme } from "@mui/material";
import Section from "./Section";
import { Link } from "gatsby";
import EmployeeContact from "./EmployeeContact";

export type FooterProps = {
  inverted?: boolean;
};
const Footer: FC<FooterProps> = ({ inverted }) => {
  const { customSpacing } = useTheme();
  return (
    <footer>
      <Section
        inverted={inverted}
        sx={{
          backgroundColor: "text.primary",
          color: "white",
          position: "relative",
          pt: customSpacing?.marginTopLarge,
          pb: customSpacing?.marginBottomLarge,
        }}
      >
        <Stack
          justifyContent="space-between"
          flexDirection="column"
          sx={{
            maxWidth: customSpacing?.containerMaxWidth,
            px: { xs: 1.5, sm: 3, md: 6 },
            mx: "auto",
          }}
        >
          <Stack
            justifyContent="space-between"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems="flex-start"
            gap={customSpacing?.gutter}
          >
            <Box flex={1}>
              <Typography variant="subtitle1" mb={2} id="contact-us-footer">
                Contact us
              </Typography>
              <Typography
                variant="body1"
                maxWidth={customSpacing?.bodyTexBlockMaxWidth}
              >
                We’d love to discuss your idea or challenge. Please get in touch
                with Charlotte, our MD, to arrange a call.
              </Typography>
              <Typography variant="subtitle1" mt={6} mb={2}>
                Mettle Studio
              </Typography>
              <Typography
                variant="body1"
                maxWidth={customSpacing?.bodyTexBlockMaxWidth}
              >
                T130 Somerset House <br /> London <br /> WC2R 1LA
              </Typography>
            </Box>
            <Box flex={1} width="100%">
              <Divider
                sx={{
                  my: customSpacing?.verticalSpacing,
                  display: { xs: "block", sm: "none" },
                }}
              />
              <Typography variant="body2" mb={1}>
                To discuss new projects
              </Typography>
              <EmployeeContact
                name="Charlotte Heard"
                role="Managing Director"
                email="charlotte@mettle-studio.com"
                image="charl-contact"
                stackProps={{ mb: 6 }}
              />
              <Typography variant="body2" mb={1}>
                For general enquires
              </Typography>
              <EmployeeContact
                name="Katy Jones"
                email="katy@mettle-studio.com"
                image="katy-contact"
                stackProps={{ mb: 6 }}
              />
              <Typography variant="body2">
                If you’re interested in working at Mettle, we’d love to hear
                from you, please email:
              </Typography>
              <MuiLink underline="always" href="mailto:hello@mettle-studio.com">
                <Typography variant="body1">hello@mettle-studio.com</Typography>
              </MuiLink>
              <Typography variant="body2" mt={{ xs: 3, sm: 7 }}>
                Press and media enquiries
              </Typography>
              <MuiLink underline="always" href="mailto:press@mettle-studio.com">
                <Typography variant="body1">press@mettle-studio.com</Typography>
              </MuiLink>
              <Divider
                sx={{
                  my: customSpacing?.verticalSpacing,
                  display: { xs: "block", sm: "none" },
                }}
              />
            </Box>
          </Stack>
          <Stack
            mt={{ xs: 0, sm: 7 }}
            justifyContent="flex-start"
            alignItems="flex-start"
            flexDirection={{ xs: "column-reverse", sm: "row" }}
            gap={customSpacing?.gutter}
          >
            <Box flex={1}>
              <Typography variant="caption" mr={2}>
                <span>Mettle - {new Date().getFullYear()}</span>
              </Typography>
              <Link to="/privacy-policy">
                <Typography variant="caption">Privacy policy</Typography>
              </Link>
            </Box>

            <Box flex={1}>
              <MuiLink
                aria-label="Mettle Studio on LinkedIn"
                color="inherit"
                href="https://www.linkedin.com/company/mettle-studio-ltd-"
                target="_blank"
              >
                <Typography variant="caption">LinkedIn</Typography>
              </MuiLink>
              <MuiLink
                ml={7}
                aria-label="Mettle Studio on Instagram"
                color="inherit"
                href="https://www.instagram.com/mettlestudio/?hl=en"
                target="_blank"
              >
                <Typography variant="caption">Instagram</Typography>
              </MuiLink>
            </Box>
          </Stack>
        </Stack>
      </Section>
    </footer>
  );
};

export default Footer;
